import * as HLF from "html-living-framework";
import * as PIXI from "pixi.js";
import {CookingActivity, CookingActivityStep} from "./CookingActivity";
import {KitchenScreen} from "../../States/KitchenScreen";
import {array, Callback, Mix} from "../../Core/Reflection";
import {pastaStep1, pastaStep2, pastaStep4, pastaStep5, pastaStep6, pastaStep7, pastaStepBase} from "../../Layout/KitchenScreenLayout";
import {Sprite} from "../../Core/Sprite";
import {Container} from "../../Core/Container";
import {Ease} from "../../Core/Animatable";
import {CookingItem} from "./CookingItem";
import {ColorRampFilter} from "../../Filters/ColorRampFilter";
import {texture, textureOrDefault} from "../../Core/AssetHelpers";
import {PngSequence} from "../../Core/PngSequence";
import {TransformBackup} from "../../Core/TransformBackup";
import {Poly} from "../../Core/Shape";
import {Signal, Times} from "../../Core/Signal";
import {IPoint, PointUtil} from "../../Core/Geom";
import {Anchor} from "../../Core/Anchor";
import {AudioChannel} from "../../Core/AudioChannel";
import {Keyboard} from "../Keyboard";
import {playVO, ReactionVO} from "../../Data/VoiceOver";

export class PastaCookingActivity extends CookingActivity {

    constructor(kitchen: KitchenScreen) {
        super(kitchen, [
            PastaCookingStep1_Noodle,
            PastaCookingStep2_Color,
            PastaCookingStep3_Cook,
            PastaCookingStep4_Serve,
            PastaCookingStep5_Sauce,
            PastaCookingStep6_Topping,
            PastaCookingStep7_Eat,
        ]);
    }
}

class PastaCookingStepBase extends CookingActivityStep {

    public pastaPot!: PastaPot;
    public noodleType = 0;
    public noodleColor = 0;
    public sauceColor = 0;
    public topping = 0;

    play(onComplete: Callback, prev?: PastaCookingStepBase): void {
        if (prev) {
            this.noodleType = prev.noodleType;
            this.noodleColor = prev.noodleColor;
            this.sauceColor = prev.sauceColor;
            this.topping = prev.topping;
        }
        this.pastaPot = this.addChildAt(prev?.pastaPot ?? new PastaPot(), 0);
        pastaStepBase.call(this);
    }
}

class PastaCookingStep1_Noodle extends PastaCookingStepBase {

    readonly noodlesOption = array((i: number) => this.addChild(new PastaOption(i + 1, 1)));
    public selectedOption?: PastaOption;

    play(onComplete: Callback, prev?: PastaCookingStepBase): void {
        super.play(onComplete, prev);
        pastaStep1.call(this);

        const hoverVO = [
            "LetsGoLuna_126",
            "LetsGoLuna_127",
            "LetsGoLuna_128",
            "LetsGoLuna_129",
            "LetsGoLuna_130",
            "LetsGoLuna_131",
            "LetsGoLuna_132",
            "LetsGoLuna_133"
        ];

        let i = 0;
        for (const option of this.noodlesOption) {
            option.hoverVO = hoverVO[i];
            option.onClick.add(() => {
                this.noodleType = option.noodleType;
                this.selectedOption = option;
                option.zIndex = -1;
                this.noodlesOption.forEach(it => it.enabled = false);
                option.squishStretch(() => {
                    const noodlesOffscreen = this.noodlesOption.filter(it => it !== option).reverse();
                    this.moveItemsToRight(noodlesOffscreen, () => {
                        noodlesOffscreen.forEach(it => it.visible = false);
                        onComplete();
                    });
                });
                Keyboard.focus(this.kitchen.buttonHome);
            });
            i++;
        }
        this.moveItemsFromLeft([...this.noodlesOption].reverse(), () => {
            playVO("LetsGoLuna_125", () => this.kitchen.playFactVO(["LetsGoLuna_158", "LetsGoLuna_159"]));
            this.noodlesOption.forEach(it => it.enabled = true);
            Keyboard.add(...this.noodlesOption);
            Keyboard.focus(this.noodlesOption[0]);
        });
    }
}

class PastaCookingStep2_Color extends PastaCookingStepBase {

    readonly colors = array((i: number) => this.addChild(new PastaOption(1, i + 1)));
    public selectedColor?: PastaOption;

    play(onComplete: Callback, prev: PastaCookingStep1_Noodle): void {
        super.play(onComplete, prev);
        pastaStep2.call(this);

        const p = prev.selectedOption?.position ?? {x: 0, y: 0};
        const hoverVO = [
            "LetsGoLuna_135",
            "LetsGoLuna_136",
            "LetsGoLuna_137",
            "LetsGoLuna_138"
        ];

        let i = 0;
        for (const color of this.colors) {
            color.hoverVO = hoverVO[i];
            color.zIndex = 0;
            color.noodleType = prev.noodleType;
            color.onClick.add(() => {
                this.noodleColor = color.noodleColor;
                this.selectedColor = color;
                color.zIndex = -1;
                this.colors.forEach(it => it.enabled = false);
                color.squishStretch(() => {
                    const colorsOffscreen = this.colors.filter(it => it !== color).reverse();
                    this.moveItemsToRight(colorsOffscreen, () => {
                        colorsOffscreen.forEach(it => it.visible = false);
                        onComplete();
                    });
                });
                Keyboard.focus(this.kitchen.buttonHome);
            });
            color.moveXYFrom(p.x, p.y, 0.6, Ease.sineInOut, i === this.colors.length - 1 ? () => {
                playVO("LetsGoLuna_134");
                this.colors.forEach(it => it.enabled = true);
                Keyboard.add(...this.colors);
                Keyboard.focus(this.colors[0]);
            } : undefined);
            i++;
        }
        prev.destroy();
    }
}

class PastaCookingStep3_Cook extends PastaCookingStepBase {

    play(onComplete: Callback, prev: PastaCookingStep2_Color) {
        super.play(onComplete, prev);

        const noodlesInPot = this.pastaPot.noodles;
        const noodles = prev.selectedColor!;
        const t = 2;
        let y = 1e6;
        let up = true;

        this.kitchen.luna.show(() => {
            playVO(["LetsGoLuna_139", "LetsGoLuna_140"], () => {
                noodles.changeParent(this.pastaPot);
                noodles.tweenTo(t, Ease.sineInOut, {
                    motionPath: {
                        path: [
                            {x: noodlesInPot.x, y: noodlesInPot.y - this.pastaPot.height * 0.3},
                            {x: noodlesInPot.x, y: noodlesInPot.y},
                        ]
                    },
                    onUpdate: () => {
                        if (up && noodles.y > y) {
                            noodles.zIndex = noodlesInPot.zIndex;
                            up = false;
                        }
                        y = noodles.y;
                    },
                    onComplete: () => {
                        prev.destroy();
                        const timer = this.kitchen.potTimer;
                        timer.show(3, () => timer.hide(onComplete));
                        timer.zIndex = -1;
                    }
                });
                noodles.scaleTo(noodlesInPot.scale.x, t, Ease.sineInOut);
                this.delayedCall(t * 0.8, () => AudioChannel.SFX.play("drop in water"));
            });
        });
    }
}

class PastaCookingStep4_Serve extends PastaCookingStepBase {

    readonly pastaPlate = this.addChild(new Sprite());
    readonly spoonInPot = new Sprite();
    readonly noodlesShape = this.addChild(new NoodlesShape(1, 1));
    readonly servingSpoon = this.addChild(new ServingSpoon(1, 1));

    play(onComplete: Callback, prev?: PastaCookingStepBase) {
        super.play(onComplete, prev);
        pastaStep4.call(this);

        const spoon = this.servingSpoon;
        const noodles = this.noodlesShape;

        spoon.visible = false;
        noodles.visible = false;

        this.moveItemsFromLeft(this.pastaPlate, () => {
            playVO("LetsGoLuna_141");
            this.pastaPot.zIndex = -1;

            spoon.changeParent(this.pastaPot);
            spoon.saveTransform();
            spoon.changeParent(this);

            spoon.visible = true;
            spoon.scale.set(this.spoonInPot.scale.x * 0.5);
            spoon.x = this.spoonInPot.x;
            spoon.y = this.spoonInPot.y;
            spoon.rotation = this.spoonInPot.rotation;
            spoon.changeParent(this.pastaPot);
            spoon.zIndex = this.pastaPot.noodles.zIndex;
            spoon.moveYFrom(spoon.y - spoon.height, 0.4, Ease.sineOut, () => {
                spoon.noodleType = this.noodleType;
                spoon.noodleColor = this.noodleColor;
                const t = 2;
                const b = spoon.getTransformBackup();
                let y = 1e6;
                let up = true;
                spoon.tweenTo(t, Ease.sineInOut, {
                    motionPath: {
                        path: [
                            {x: spoon.x, y: spoon.y - spoon.height},
                            {x: b.x, y: b.y},
                        ]
                    },
                    onUpdate: () => {
                        if (up && spoon.y > y) {
                            spoon.zIndex = -1;
                            up = false;
                        }
                        y = spoon.y;
                    }
                });
                spoon.tweenTo(t, Ease.sineInOut, {
                    scaleX: b.scaleX,
                    scaleY: b.scaleY,
                    rotation: b.rotation,
                    onComplete: () => {
                        spoon.hideNoodles();
                        spoon.changeParent(this);
                        noodles.noodleType = this.noodleType;
                        noodles.noodleColor = this.noodleColor;
                        noodles.visible = true;
                        noodles.alphaFrom(0, 0.2);
                        noodles.scaleFrom(0, 0.6, Ease.backOut);
                        spoon.tweenTo(0.8, Ease.backOut.config(4), {
                            rotation: 20 * Math.PI / 180,
                            y: spoon.y - spoon.height * 0.6,
                            onComplete: () => {
                                this.moveItemsToRight(spoon, () => this.kitchen.luna.hide(onComplete));
                            }
                        });
                    }
                });
            });
        });

        prev?.destroy();
    }
}

class PastaCookingStep5_Sauce extends PastaCookingStepBase {

    readonly pastaPlate = this.addChild(new Sprite());
    readonly noodlesShape = this.addChild(new NoodlesShape(1, 1));
    readonly pastaSauce = this.addChild(new PastaSauce(1));
    readonly sauceButton = array((i: number) => this.addChild(new SauceButton(i + 1)));

    play(onComplete: Callback, prev?: PastaCookingStepBase) {
        super.play(onComplete, prev);
        pastaStep5.call(this);

        const sauce = this.pastaSauce;
        const buttons = this.sauceButton;
        const hoverVO = [
            "LetsGoLuna_143",
            "LetsGoLuna_144",
            "LetsGoLuna_145",
            "LetsGoLuna_146"
        ];

        this.noodlesShape.noodleType = this.noodleType;
        this.noodlesShape.noodleColor = this.noodleColor;

        let i = 0;
        for (const button of buttons) {
            button.hoverVO = hoverVO[i];
            button.enabled = false;
            button.draggable = false;
            button.onClick.add(() => {
                buttons.forEach(it => it.enabled = false);
                button.squishStretch();
                this.sauceColor = button.sauceColor;
                sauce.sauceColor = button.sauceColor;
                sauce.scaleFrom(0, 0.6, Ease.backOut, () => {
                    this.moveItemsToRight([...buttons].reverse(), onComplete);
                });
                Keyboard.focus(this.kitchen.buttonHome);
            });
            i++;
        }

        this.moveItemsFromLeft([...buttons].reverse(), () => {
            playVO("LetsGoLuna_142");
            buttons.forEach(it => it.enabled = true);
            Keyboard.add(...buttons);
            Keyboard.focus(buttons[0]);
        });

        prev?.destroy();
    }
}

class PastaCookingStep6_Topping extends PastaCookingStepBase {

    readonly pastaPlate = this.addChild(new Sprite());
    readonly noodlesShape = this.addChild(new NoodlesShape(1, 1));
    readonly pastaSauce = this.addChild(new PastaSauce(1));
    readonly pastaTopping = this.addChild(new PastaTopping());
    readonly toppingBowl = array((i: number) => this.addChild(new ToppingBowl(i + 1)));

    play(onComplete: Callback, prev?: PastaCookingStepBase) {
        super.play(onComplete, prev);
        pastaStep6.call(this);

        this.pastaSauce.sauceColor = this.sauceColor;
        this.noodlesShape.noodleType = this.noodleType;
        this.noodlesShape.noodleColor = this.noodleColor;
        this.pastaTopping.visible = false;

        const bowls = this.toppingBowl;
        const bowlsLeft = bowls.filter(it => it.x < 0);
        const bowlsRight = bowls.filter(it => it.x > 0);
        const hoverVO = [
            "LetsGoLuna_149",
            "LetsGoLuna_150",
            "LetsGoLuna_148",
            "LetsGoLuna_153",
            "LetsGoLuna_151",
            "LetsGoLuna_152_alt"
        ];

        let i = 0;
        for (const bowl of bowls) {
            const onSelectTopping = () => {
                if (this.topping === bowl.topping) {
                    return;
                }

                // Fixed quick tapping on bowls
                bowls.forEach(it => it.enabled = false);
                this.delayedCall(0.2, () => bowls.forEach(it => it.enabled = true));

                this.topping = bowl.topping;
                this.pastaTopping.topping = bowl.topping;
                this.pastaTopping.visible = true;
                this.pastaTopping.alphaFrom(0, 0.2);
                this.kitchen.confirmCookingStep(() => {
                    bowls.forEach(it => it.enabled = false);
                    this.moveItemsToLeft(bowlsLeft);
                    this.moveItemsToRight(bowlsRight, onComplete);
                });
            };
            bowl.hoverVO = hoverVO[i];
            bowl.enabled = false;
            let ignoreOnDragged = false;
            let topping: ToppingDrag;
            const startDragTopping = (p: IPoint) => {
                topping = this.addChild(new ToppingDrag(bowl.topping));
                topping.startDrag(p);
                bowl.squishStretch();
            };
            bowl.onStartDrag.add((e: PIXI.InteractionEvent) => {
                startDragTopping(e.data.getLocalPosition(this));
            });
            bowl.onVirtualDrag.add(data => {
                const p = data.getLocalPosition(this);
                topping.x = p.x;
                topping.y = p.y;
            });
            bowl.onDragged.add(() => {
                if (ignoreOnDragged) {
                    ignoreOnDragged = false;
                    return;
                }
                const s = 1000;
                const p = {x: this.pastaTopping.x, y: this.pastaTopping.y};
                const d = PointUtil.distance(topping, p);
                if (d > this.pastaPlate.width * 0.5) {
                    topping.alphaTo(0, 0.2, () => topping.destroy());
                    return;
                }
                topping.moveXYTo(p.x, p.y, d / s, Ease.sineInOut, () => {
                    topping.alphaTo(0, 0.2, () => topping.destroy());
                    onSelectTopping();
                });
            });
            bowl.onClick.add(() => {
                onSelectTopping();
            });
            bowl.onKeyboard.add(() => {
                ignoreOnDragged = true;
                bowl.enabled = true;
            });
            i++;
        }

        const onDropBowls = Times(bowls.length, () => {
            playVO("LetsGoLuna_147");
            bowls.forEach(it => it.enabled = true);
            Keyboard.add(...bowls);
            Keyboard.focus(bowls[0]);
        });
        let d = 0;
        for (const bowl of bowls) {
            const s = bowl.scale.x;
            bowl.moveYFrom(this.kitchen.top - bowl.height, 0.4, Ease.sineIn, {
                delay: d += 0.2,
                onComplete: () => {
                    bowl.tweenTo(0.1, Ease.sineIn, {
                        scaleX: s * 1.1,
                        scaleY: s * 0.9,
                        onComplete: () => {
                            bowl.tweenTo(0.2, Ease.backOut, {
                                scaleX: s,
                                scaleY: s,
                                onComplete: onDropBowls
                            })
                        }
                    })
                }
            });
        }

        prev?.destroy();
    }
}

class PastaCookingStep7_Eat extends PastaCookingStepBase {

    readonly pastaPlate = this.addChild(new Sprite());
    readonly noodlesShape = this.addChild(new NoodlesShape(1, 1));
    readonly pastaSauce = this.addChild(new PastaSauce(1));
    readonly pastaTopping = this.addChild(new PastaTopping());
    readonly button = this.addChild(new CookingItem());
    readonly pastaMask = array(() => []);

    play(onComplete: Callback, prev?: PastaCookingStepBase) {
        super.play(onComplete, prev);
        pastaStep7.call(this);

        this.pastaTopping.topping = this.topping;
        this.pastaSauce.sauceColor = this.sauceColor;
        this.noodlesShape.noodleType = this.noodleType;
        this.noodlesShape.noodleColor = this.noodleColor;

        this.button.y = this.noodlesShape.y;
        const pasta = [this.noodlesShape, this.pastaSauce, this.pastaTopping];
        const masks = this.pastaMask.map(it => this.addChild(new Poly(it)));
        pasta.forEach(it => it.changeParent(this.button.visuals));
        masks.forEach(it => {
            it.changeParent(this.button.visuals);
            it.visible = false;
        });

        const eatingVO = [
            "LetsGoLuna_154",
            "LetsGoLuna_155",
            "LetsGoLuna_156",
            "LetsGoLuna_157"];
        HLF.ArrayUtils.shuffle(eatingVO);
        playVO("LetsGoLuna_022");

        let i = 0;
        this.button.pulse();
        this.button.draggable = false;
        this.button.onClick.add(() => {
            AudioChannel.SFX.play("chewing");
            this.button.stopPulse();
            if (i < this.pastaMask.length) {
                masks.forEach(it => it.visible = false);
                masks[i].visible = true;
                pasta.forEach(it => it.mask = masks[i]);
                i++;
            } else {
                this.button.visible = false;
                this.button.enabled = false;
                Keyboard.focus(this.kitchen.buttonHome);
                const vo = eatingVO.pop();
                if (vo) {
                    playVO([...ReactionVO(), vo], onComplete);
                } else {
                    onComplete();
                }
            }
        });
        Keyboard.add(this.button);
        Keyboard.focus(this.button);

        prev?.destroy();
    }
}

class PastaTopping extends CookingItem {

    readonly pastaTopping = array(() => this.visuals.addChild(new Sprite()));
    private _topping = 0;

    constructor() {
        super();
        this.enabled = false;
    }

    get topping(): number {
        return this._topping;
    }

    set topping(value: number) {
        this._topping = value;
        this.pastaTopping.forEach((it, i) => it.visible = i + 1 === value);
    }
}

class ToppingBowl extends CookingItem {

    readonly onVirtualDrag = Signal<(data: PIXI.InteractionData) => void>();
    readonly topping;

    constructor(topping: number) {
        super();
        this.topping = topping;
        this.draggable = false;
    }
}

class ToppingDrag extends Sprite {

    constructor(topping: number) {
        super(`topping-drag-${topping}`, Anchor.middleCenter);
        this.scale.set(2);
    }

    startDrag(p: IPoint) {
        this.x = p.x;
        this.y = p.y;
        this.alpha = 1;
        this.alphaFrom(0, 0.2);
    }
}

class SauceButton extends CookingItem {

    readonly sauceColor;

    constructor(sauceColor: number) {
        super();
        this.sauceColor = sauceColor;
    }
}

class PastaSauce extends Sprite {

    private _sauceColor = 0;

    constructor(sauceColor: number) {
        super();
        this.sauceColor = sauceColor;
        this.visible = false;
    }

    get sauceColor(): number {
        return this._sauceColor;
    }

    set sauceColor(value: number) {
        this._sauceColor = value;
        const t = texture(`sauce-color-${value}`);
        if (t) {
            this.filters = [new ColorRampFilter(t)];
            this.visible = true;
        } else {
            this.visible = false;
        }
    }
}

class NoodlesShape extends Container {

    readonly noodlesShape = array(() => this.addChild(new Sprite()));

    private _noodleType = 0;
    private _noodleColor = 0;

    constructor(noodleType: number, noodleColor: number) {
        super();
        this.noodleType = noodleType;
        this.noodleColor = noodleColor;
    }

    get noodleType(): number {
        return this._noodleType;
    }

    set noodleType(value: number) {
        this._noodleType = value;
        this.noodlesShape.forEach((it, i) => it.visible = value === i + 1);
    }

    get noodleColor(): number {
        return this._noodleColor;
    }

    set noodleColor(value: number) {
        this._noodleColor = value;
        this.noodlesShape.forEach(it => it.filters = [new ColorRampFilter(textureOrDefault(`noodles-color-${value}`))]);
    }
}

class ServingSpoon extends Mix(Container, TransformBackup) {

    readonly sprite = this.addChild(new Sprite());
    readonly noodlesContainer = this.addChild(new Container());
    readonly spoonNoodles = array(() => this.noodlesContainer.addChild(new (class extends Container {
        readonly noodles = array(() => this.addChild(new Sprite()));
    })()));

    private _noodleType = 0;
    private _noodleColor = 0;

    constructor(noodleType: number, noodleColor: number) {
        super();
        this.noodleType = noodleType;
        this.noodleColor = noodleColor;
        this.noodlesContainer.visible = false;
    }

    get noodleType(): number {
        return this._noodleType;
    }

    set noodleType(value: number) {
        this._noodleType = value;
        this.noodlesContainer.visible = true;
        this.spoonNoodles.forEach((it, i) => it.visible = value === i + 1);
    }

    get noodleColor(): number {
        return this._noodleColor;
    }

    set noodleColor(value: number) {
        this._noodleColor = value;
        this.spoonNoodles.forEach(it => it.filters = [new ColorRampFilter(textureOrDefault(`noodles-color-${value}`))]);
    }

    hideNoodles() {
        this.noodlesContainer.alphaTo(0, 0.2);
    }
}

class PastaPot extends Container {

    readonly pastaPotBack = this.addChild(new Sprite());
    readonly noodles = this.addChild(new Sprite());
    readonly bubbles = this.addChild(new Container());
    readonly pastaPotFront = this.addChild(new Sprite());
    readonly bubblesBounds = {x: 0, y: 0, width: 0, height: 0};
    private bubblesInterval: number;

    constructor() {
        super();
        this.noodles.visible = false;

        this.bubblesInterval = window.setInterval(() => {
            const r = this.bubblesBounds;
            for (let i = 0, n = HLF.Random.intBetween(1, 3); i < n; i++) {
                const bubble = this.bubbles.addChild(new Bubble());
                bubble.x = HLF.Random.floatBetween(r.x, r.x + r.width);
                bubble.y = HLF.Random.floatBetween(r.y, r.y + r.height);
            }
        }, 300);
    }

    destroy() {
        clearInterval(this.bubblesInterval);
        super.destroy();
    }
}

class Bubble extends PngSequence {

    constructor() {
        super("bubbles-{1}");
        this.anchor.x = 0.5;
        this.anchor.y = 1;
        this.loop = false;
        this.scale.set(2);
        this.animationSpeed = 0.2;
        this.onComplete = () => this.destroy();
        this.play();
    }
}

class PastaOption extends CookingItem {

    private _noodleType = 0;
    private _noodleColor = 0;

    constructor(noodleType: number, noodleColor: number) {
        super();
        this.noodleType = noodleType;
        this.noodleColor = noodleColor;
        this.draggable = false;
        this.enabled = false;
    }

    get noodleType(): number {
        return this._noodleType;
    }

    set noodleType(value: number) {
        this._noodleType = value;
        this.sprite.texture = textureOrDefault(`noodles-option-${value}`);
    }

    get noodleColor(): number {
        return this._noodleColor;
    }

    set noodleColor(value: number) {
        this._noodleColor = value;
        this.sprite.filters = [new ColorRampFilter(textureOrDefault(`noodles-color-${value}`))];
    }
}